import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Send, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  "Roof Leak Repair",
  "Emergency Roof Repair",
  "Storm Damage Repair",
  "Roof Replacement",
  "Shingle Repair",
  "Commercial Roofing",
  "Other",
];

export default function ContactForm({ variant = "default" }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service_needed: "",
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!/^\+?[\d\s-]{10,}$/.test(formData.phone.trim())) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.service_needed) newErrors.service_needed = "Please select a service";
    if (!formData.consent) newErrors.consent = "Please agree to the data processing";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, formData);
      setIsSubmitted(true);
      toast.success("Thank you! We'll contact you shortly.");
      setFormData({
        name: "",
        phone: "",
        email: "",
        service_needed: "",
        message: "",
        consent: false,
      });
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12 animate-fadeInUp" data-testid="form-success">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h3 className="heading-h3 text-slate-900 mb-4">Thank You!</h3>
        <p className="text-body max-w-md mx-auto">
          Your request has been received. Our team will contact you within 24 hours 
          to schedule your free inspection.
        </p>
        <Button 
          className="btn-secondary mt-6" 
          onClick={() => setIsSubmitted(false)}
          data-testid="submit-another"
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  const isDark = variant === "dark";

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <label className={`form-label ${isDark ? "text-slate-300" : ""}`}>
            Full Name *
          </label>
          <Input
            type="text"
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className={`form-input ${errors.name ? "border-red-500" : ""}`}
            data-testid="input-name"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className={`form-label ${isDark ? "text-slate-300" : ""}`}>
            Phone Number *
          </label>
          <Input
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            className={`form-input ${errors.phone ? "border-red-500" : ""}`}
            data-testid="input-phone"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>
      </div>

      {/* Email */}
      <div>
        <label className={`form-label ${isDark ? "text-slate-300" : ""}`}>
          Email Address *
        </label>
        <Input
          type="email"
          placeholder="john@example.com"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          className={`form-input ${errors.email ? "border-red-500" : ""}`}
          data-testid="input-email"
        />
        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
      </div>

      {/* Service Needed */}
      <div>
        <label className={`form-label ${isDark ? "text-slate-300" : ""}`}>
          Service Needed *
        </label>
        <Select
          value={formData.service_needed}
          onValueChange={(value) => handleChange("service_needed", value)}
        >
          <SelectTrigger 
            className={`form-input ${errors.service_needed ? "border-red-500" : ""}`}
            data-testid="select-service"
          >
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {services.map((service) => (
              <SelectItem key={service} value={service}>
                {service}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.service_needed && (
          <p className="text-red-500 text-sm mt-1">{errors.service_needed}</p>
        )}
      </div>

      {/* Message */}
      <div>
        <label className={`form-label ${isDark ? "text-slate-300" : ""}`}>
          Message (Optional)
        </label>
        <Textarea
          placeholder="Tell us about your roofing issue..."
          value={formData.message}
          onChange={(e) => handleChange("message", e.target.value)}
          className="form-textarea"
          data-testid="input-message"
        />
      </div>

      {/* Consent Checkbox */}
      <div className="flex items-start gap-3">
        <Checkbox
          id="consent"
          checked={formData.consent}
          onCheckedChange={(checked) => handleChange("consent", checked)}
          className={errors.consent ? "border-red-500" : ""}
          data-testid="checkbox-consent"
        />
        <label 
          htmlFor="consent" 
          className={`text-sm cursor-pointer ${isDark ? "text-slate-400" : "text-slate-600"}`}
        >
          I agree to the processing of my personal data in accordance with the{" "}
          <a href="/privacy-policy" className="text-orange-500 hover:underline">
            Privacy Policy
          </a>
          . *
        </label>
      </div>
      {errors.consent && <p className="text-red-500 text-sm -mt-4">{errors.consent}</p>}

      {/* Submit Button */}
      <Button
        type="submit"
        className="btn-primary w-full"
        disabled={isSubmitting}
        data-testid="submit-form"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Get Free Estimate
          </>
        )}
      </Button>

      <p className={`text-xs text-center ${isDark ? "text-slate-500" : "text-slate-400"}`}>
        We respect your privacy. No spam, ever.
      </p>
    </form>
  );
}
